import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchProCountForLocations } from "../../utils/proCount";
import {
  getMyBusinesses,
  getMyShopsV2,
  createLocation,
} from "../../services/businessService";
import SidebarLayout from "./SidebarLayout";
import BusinessSelector from "../../components/BusinessSelector";
import ProfessionalProfileBanner from "../../components/ProfessionalProfileBanner";
// THE DAY AT A GLANCE (2026-10-23) — above the four tiles, as the user asked: one grid per location, hours down
// the side, professionals across, R1 choosing the location (and a single location needing no choice).
import DayScheduleGrid from "../../components/DayScheduleGrid";
import OnboardingChecklist from "../../components/OnboardingChecklist";
import GoLivePanel from "../../components/GoLivePanel";
import TrialCountdown from "../../components/TrialCountdown";
import ShareProfileButton from "../../components/ShareProfileButton";
import AddressAutocompleteInput from "../../components/AddressAutocompleteInput";
import {
  getOwnerCompletedSteps,
  shouldShowOnboarding,
} from "../../utils/onboardingHelpers";
import axios from "axios";
import WalkInButton from "../../components/WalkInButton";
import "../../shop_registration_styles.css";
import "./ShopDashboardModern.css";
import { TIMEZONE_OPTIONS } from "../../constants/timezones";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function formatBusinessType(str) {
  if (!str) return "";
  // Convert snake_case to Title Case (hair_salon -> Hair Salon)
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function capitalizeFirst(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}

function ShopDashboardPage() {
  const navigate = useNavigate();
  const currentRole = localStorage.getItem("ogestyle_role");
  const isR2 = currentRole === "R2";
  const [businesses, setBusinesses] = useState([]); // Phase 3: User's businesses
  const [selectedBusinessId, setSelectedBusinessId] = useState(() => {
    // Restore from localStorage if available
    return localStorage.getItem("ogestyle_selectedBusinessId") || null;
  }); // Phase 3: Current business
  const [shops, setShops] = useState([]);
  const [businessInfo, setBusinessInfo] = useState([]);
  const [onboardingData, setOnboardingData] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [connectingShopId, setConnectingShopId] = useState(null);
  const [proCounts, setProCounts] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [userRoles, setUserRoles] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSuccess, setAddSuccess] = useState(false);
  const [error, setError] = useState("");
  const [subscribedBanner, setSubscribedBanner] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [showProrationModal, setShowProrationModal] = useState(false);
  const [prorationData, setProrationData] = useState(null);
  const [newLocationData, setNewLocationData] = useState(null);
  const [prorationLoading, setProrationLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [formData, setFormData] = useState({
    type: [],
    locationMode: "hybrid",
    street: "",
    city: "",
    state: "",
    zipCode: "",
    phone: "",
    email: "",
    timezone: "",
    lat: null,
    lng: null,
  });

  // The location's timezone is deliberately NOT detected or pre-filled.
  //
  // Pre-selecting a value - even a correct detection - lets the owner submit
  // without ever making the choice, and hides a WRONG detection (travel, VPN,
  // mismatched locale) behind a value that looks plausible. It must be actively
  // chosen (policy 2026-10-03).

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionSuccess = urlParams.get("success");
    const subscriptionCanceled = urlParams.get("canceled");

    if (subscriptionSuccess === "true") {
      alert("🎉 Subscription activated successfully! Welcome aboard!");
      window.history.replaceState({}, document.title, "/dashboard");
      setTimeout(() => window.location.reload(), 1000);
    } else if (subscriptionCanceled === "true") {
      alert(
        "⚠️ Checkout canceled. You can subscribe anytime to continue using the platform.",
      );
      window.history.replaceState({}, document.title, "/dashboard");
    }

    if (urlParams.get("subscribed") === "true") {
      setSubscribedBanner(true);
      window.history.replaceState({}, document.title, "/dashboard");
      setTimeout(() => setSubscribedBanner(false), 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch pro counts for all locations after shops are loaded
  useEffect(() => {
    const fetchCounts = async () => {
      if (!shops.length) return;
      const token = localStorage.getItem("ogestyle_token");
      const locationIds = shops.map((s) => s._id);
      const counts = await fetchProCountForLocations(locationIds, token);
      setProCounts(counts);
    };
    fetchCounts();
  }, [shops]);

  // Phase E1: Detect completed steps when data changes
  useEffect(() => {
    if (!onboardingData || !businesses || !shops) return;

    const selectedBusiness =
      businesses.find((b) => b._id === selectedBusinessId) || businesses[0];
    const businessLocations = shops.filter(
      (s) => s.businessId === (selectedBusiness?._id || selectedBusinessId),
    );
    const stripeStatus = businessInfo.find(
      (b) => b.businessId === (selectedBusiness?._id || selectedBusinessId),
    )?.stripeStatus;
    const stripeConnected =
      stripeStatus?.connected &&
      stripeStatus?.chargesEnabled &&
      stripeStatus?.payoutsEnabled;

    const detected = getOwnerCompletedSteps(
      null, // user not needed anymore
      selectedBusiness,
      businessLocations,
      [], // services not needed for R1
      stripeConnected,
    );

    const mergedSteps = [
      ...new Set([...(onboardingData.completedSteps || []), ...detected]),
    ];
    setCompletedSteps(mergedSteps);

    const allSteps = ["profile", "location", "hours", "payment"];
    setShowOnboarding(
      shouldShowOnboarding(onboardingData, mergedSteps, allSteps),
    );
  }, [onboardingData, businesses, shops, selectedBusinessId, businessInfo]);

  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [businessStripeStatus, setBusinessStripeStatus] = useState({}); // businessId -> status
  const [loadingStripe, setLoadingStripe] = useState(true);
  const [smsCredits, setSmsCredits] = useState(null);
  const [loadingSmsCredits, setLoadingSmsCredits] = useState(false);

  const fetchBusinessTypes = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/business-categories`,
      );
      const normalizedTypes = Array.isArray(res?.data?.types)
        ? res.data.types
        : Array.isArray(res?.data?.categories)
          ? res.data.categories
          : [];
      setBusinessTypes(normalizedTypes);
    } catch (error) {
      // Fallback to default types
      setBusinessTypes([
        { value: "barber_shop", label: "Barber Shop" },
        { value: "hair_salon", label: "Hair Salon" },
        { value: "nail_salon", label: "Nail Salon" },
        { value: "spa", label: "Spa" },
      ]);
    }
  };
  useEffect(() => {
    fetchBusinessTypes();
    fetchShops();
    // eslint-disable-next-line
  }, []);

  const fetchShops = async () => {
    setLoading(true);
    setLoadingStripe(true);
    try {
      // Phase 3: Use new business-centric endpoint
      const businessesData = await getMyBusinesses();
      setBusinesses(businessesData);

      // Set initial selected business from localStorage or first one
      const savedBusinessId = localStorage.getItem(
        "ogestyle_selectedBusinessId",
      );
      const businessExists = businessesData.find(
        (b) => b._id === savedBusinessId,
      );

      if (savedBusinessId && businessExists) {
        // Restore saved selection if business still exists
        setSelectedBusinessId(savedBusinessId);
      } else if (businessesData.length > 0) {
        // Set to first business if no saved selection or saved business doesn't exist
        const firstBusinessId = businessesData[0]._id;
        setSelectedBusinessId(firstBusinessId);
        localStorage.setItem("ogestyle_selectedBusinessId", firstBusinessId);
      } else {
        // No businesses found, clear selection
        setSelectedBusinessId(null);
        localStorage.removeItem("ogestyle_selectedBusinessId");
      }

      // Phase 3: Use businessService to get shops (properly handles nested structure)
      const shopsList = await getMyShopsV2();
      setShops(shopsList);

      // Group shops by business and fetch Stripe status once per business
      const businessMap = new Map();
      shopsList.forEach((shop) => {
        if (shop.businessId && !businessMap.has(shop.businessId)) {
          businessMap.set(shop.businessId, {
            businessId: shop.businessId,
            businessName: shop.businessName || "Business",
            locationCount: shopsList.filter(
              (s) => s.businessId === shop.businessId,
            ).length,
            sampleShopId: shop._id, // Use for API calls
          });
        }
      });

      const businesses = Array.from(businessMap.values());
      setBusinessInfo(businesses);

      // Fetch Stripe status once per business
      await fetchBusinessStripeStatuses(businesses, shopsList);
    } catch (error) {
      if (error?.response?.status !== 401 && error?.response?.status !== 403) {
      }
      setShops([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessStripeStatuses = async (businesses, shopsList) => {
    const token = localStorage.getItem("ogestyle_token");
    const businessStatusMap = {};

    // Fetch Stripe status for each business
    for (const business of businesses) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/stripe/connect-status/${business.sampleShopId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        businessStatusMap[business.businessId] = response.data;
      } catch (error) {
        if (
          error?.response?.status !== 401 &&
          error?.response?.status !== 403
        ) {
        }
        businessStatusMap[business.businessId] = {
          connected: false,
          status: "error",
        };
      }
    }

    // Update businessInfo with Stripe status
    const updatedBusinessInfo = businesses.map((biz) => ({
      ...biz,
      stripeStatus: businessStatusMap[biz.businessId] || { connected: false },
    }));
    setBusinessInfo(updatedBusinessInfo);
    setBusinessStripeStatus(businessStatusMap);
    setLoadingStripe(false);
  };

  // Phase E1: Fetch onboarding status
  const fetchOnboardingStatus = async () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/onboarding/status`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOnboardingData(response.data);
    } catch (error) {
      if (error?.response?.status !== 401 && error?.response?.status !== 403) {
      }
    }
  };

  // Phase E1: Handle onboarding step click
  const handleStepClick = async (step) => {
    navigate(step.action);
  };

  // Phase E1: Handle onboarding dismiss
  const handleDismissOnboarding = async () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/onboarding/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowOnboarding(false);
    } catch (error) {
    }
  };

  // Fetch SMS credits for selected business
  const fetchSmsCredits = async (businessId) => {
    if (!businessId) return;

    setLoadingSmsCredits(true);
    const token = localStorage.getItem("ogestyle_token");

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/sms-credits/business/${businessId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSmsCredits(response.data.credits);
    } catch (error) {
      if (error?.response?.status !== 401 && error?.response?.status !== 403) {
      }
      setSmsCredits(null);
    } finally {
      setLoadingSmsCredits(false);
    }
  };

  // Fetch SMS credits when selected business changes
  useEffect(() => {
    if (selectedBusinessId) {
      fetchSmsCredits(selectedBusinessId);
    }
  }, [selectedBusinessId]);

  const handleConnectStripe = async (shopId) => {
    const token = localStorage.getItem("ogestyle_token");
    setConnectingShopId(shopId);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/stripe/connect-account`,
        { shopId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.onboardingUrl) {
        // Redirect to Stripe onboarding
        window.location.href = response.data.onboardingUrl;
      }
    } catch (error) {
      alert(
        error.response?.data?.message ||
          "Failed to connect Stripe. Please try again.",
      );
      setConnectingShopId(null);
    }
  };

  const handleOpenStripeDashboard = async (shopId) => {
    const token = localStorage.getItem("ogestyle_token");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/stripe/create-login-link/${shopId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.url) {
        window.open(response.data.url, "_blank");
      }
    } catch (error) {
      alert("Failed to open Stripe dashboard. Please try again.");
    }
  };

  // Add Shop Modal logic - formData already declared above
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStreetChange = (street) => {
    setFormData((prev) => ({
      ...prev,
      street,
      lat: "",
      lng: "",
    }));
  };

  const handleAddressSelect = (address) => {
    setFormData((prev) => ({
      ...prev,
      street: address.street || prev.street,
      city: address.city || prev.city,
      state: address.state || prev.state,
      zipCode: address.zip || prev.zipCode,
      lat: address.lat ?? "",
      lng: address.lng ?? "",
    }));
  };

  const handleTypeChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      type: checked
        ? [...prev.type, value]
        : prev.type.filter((t) => t !== value),
    }));
  };

  const handleAddShop = async (e) => {
    e.preventDefault();

    // Never submit without an actively chosen timezone (browser guard, not backend).
    if (!formData.timezone || !formData.timezone.trim()) {
      setError("Please choose your timezone.");
      return;
    }

    setError("");
    const token = localStorage.getItem("ogestyle_token");

    // Phase 3: Use selected business instead of fetching by email
    if (!selectedBusinessId) {
      setError("No business selected. Please select a business first.");
      return;
    }

    const selectedBusiness = businesses.find(
      (b) => b._id === selectedBusinessId,
    );
    if (!selectedBusiness) {
      setError("Selected business not found. Please try again.");
      return;
    }

    if (!formData.lat || !formData.lng) {
      setError(
        "Coordinates (lat/lng) are required. Please select a valid address from the suggestions.",
      );
      return;
    }

    const allowedModes = [
      "commission_only",
      "booth_rental_only",
      "hybrid",
    ];
    if (!allowedModes.includes(formData.locationMode)) {
      setError(
        "Please select a valid location mode: Commission Only, Hybrid, or Booth Rental Only.",
      );
      return;
    }

    try {
      const subResponse = await axios.get(`${API_BASE_URL}/api/subscription-new/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { trialStatus, hasSubscription, status } = subResponse.data;
      const normalizedTrialStatus = (trialStatus || "").toLowerCase();
      const hasActiveSub = hasSubscription === true && status === "active";
      const isTrialExpired = normalizedTrialStatus === "expired";

      if (isTrialExpired && !hasActiveSub) {
        setError("Your free trial has ended. Please subscribe to add more locations.");
        setShowAddModal(false);
        setTimeout(() => {
          navigate("/dashboard/settings/subscription/checkout?reason=add_location");
        }, 2000);
        return;
      }

      const userResponse = await axios.get(`${API_BASE_URL}/api/users/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const ownerEmail = userResponse.data?.email || "";
      if (!ownerEmail) {
        setError("Could not determine owner email. Please ensure you are logged in.");
        return;
      }

      const businessEmail = selectedBusiness.businessEmail || ownerEmail;
      const locationEmail = formData.email.trim() || businessEmail;

      const data = {
        ownerEmail,
        businessId: selectedBusinessId,
        businessName: selectedBusiness.name,
        shopName: selectedBusiness.name,
        type: formData.type,
        locationMode: formData.locationMode,
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        phone: formData.phone,
        email: locationEmail,
        timezone: formData.timezone,
        lat: formData.lat,
        lng: formData.lng,
      };

      const newLocation = await createLocation(data);

      if (!hasActiveSub) {
        setShowAddModal(false);
        setAddSuccess(true);
        await fetchShops();
        return;
      }

      const prorationPreview = await axios.post(
        `${API_BASE_URL}/api/subscription-new/preview-add-location`,
        { locationId: newLocation._id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setNewLocationData(newLocation);
      setProrationData(prorationPreview.data);
      setShowAddModal(false);
      setShowProrationModal(true);
    } catch (err) {
      setError(err.response?.data?.message || "Error adding location");
    }
  };

  const handleConfirmProration = async () => {
    setProrationLoading(true);
    const token = localStorage.getItem("ogestyle_token");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/subscription-new/add-location`,
        { locationId: newLocationData._id },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        setShowProrationModal(false);
        setAddSuccess(true);
        setProrationData(null);
        setNewLocationData(null);
        await fetchShops();
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to add location to subscription",
      );
      setShowProrationModal(false);
    } finally {
      setProrationLoading(false);
    }
  };

  const handleCancelProration = async () => {
    setShowProrationModal(false);
    setProrationData(null);
    setNewLocationData(null);
    await fetchShops();
  };

  const handleAddAnother = () => {
    setFormData({
      type: [],
      locationMode: "hybrid",
      street: "",
      city: "",
      state: "",
      zipCode: "",
      phone: "",
      email: "",
      // Not detected or pre-filled - the owner must choose it for the new location.
      timezone: "",
      lat: "",
      lng: "",
    });

      setAddSuccess(true);
    setAddSuccess(false);
    setShowAddModal(true);
    setError("");
  };

  if (isR2) {
    const r2Cards = [
      { title: "Bookings", subtitle: "Manage location bookings", path: "/dashboard/bookings", icon: "📖" },
      { title: "Professionals", subtitle: "Manage location professionals", path: "/dashboard/professionals", icon: "👩‍💼" },
      { title: "Customers", subtitle: "View and manage customers", path: "/dashboard/customers", icon: "👥" },
      { title: "Services", subtitle: "Manage service offerings", path: "/dashboard/services", icon: "🧰" },
      { title: "Products", subtitle: "Manage product catalog", path: "/dashboard/products", icon: "📦" },
      { title: "Orders", subtitle: "Track and process orders", path: "/dashboard/orders", icon: "🛍️" },
      { title: "Marketing", subtitle: "Run campaigns and outreach", path: "/dashboard/marketing", icon: "📨" },
      { title: "Reviews", subtitle: "Monitor customer reviews", path: "/dashboard/reviews", icon: "⭐" },
      { title: "Waitlist", subtitle: "Manage waitlisted clients", path: "/dashboard/waitlist", icon: "📝" },
      { title: "Looks", subtitle: "Manage business looks", path: "/dashboard/looks", icon: "🖼️" },
      { title: "Settings", subtitle: "Manage your personal profile", path: "/dashboard/admin/profile", icon: "⚙️" },
    ];

    return (
      <SidebarLayout>
        <div className="dashboard-content">
          <div style={{ marginBottom: "24px" }}>
            <h2 style={{ marginBottom: "8px", fontSize: "24px", fontWeight: "600" }}>
              Personal Admin Dashboard
            </h2>
            <p style={{ color: "#6b7280", fontSize: "14px" }}>
              Your access is limited to your assigned location and personal admin tools.
            </p>

            {/* Walk-in (Phase 1): the R2 branch returns early, so the button has
                to be added here as well or admins would never see it. R2 cannot
                be the provider of record, so a professional must be chosen. */}
            <div style={{ marginTop: "16px" }}>
              <WalkInButton
                requireProvider
                shopId={shops[0]?._id || null}
                locations={shops}
                role="R2"
              />
            </div>
          </div>

          {/* THE DAY, ABOVE THE CARDS (user, 2026-10-23: *"while it will be above the cards in R2"*).
              NO PICKER: an R2 is assigned to a location, so there is nothing for them to choose — the grid
              opens on the location they actually work in, and the component falls back to the first assigned
              location when the list arrives after this renders. */}
          <DayScheduleGrid
            locations={shops.map((location) => ({
              _id: location._id,
              name: location.name,
            }))}
            allowLocationPicker={false}
            title="Today at a glance"
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
              gap: "16px",
            }}
          >
            {r2Cards.map((card) => (
              <button
                key={card.path}
                type="button"
                className="dashboard-card"
                onClick={() => navigate(card.path)}
                style={{
                  textAlign: "left",
                  padding: "18px",
                  border: "1px solid #e5e7eb",
                  background: "white",
                  cursor: "pointer",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 8px 20px rgba(17, 24, 39, 0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div style={{ fontSize: "20px", marginBottom: "8px" }}>{card.icon}</div>
                <div style={{ fontSize: "16px", fontWeight: "600", color: "#111827", marginBottom: "6px" }}>
                  {card.title}
                </div>
                <div style={{ fontSize: "13px", color: "#6b7280" }}>{card.subtitle}</div>
              </button>
            ))}
          </div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="dashboard-content shop-dashboard-modern">
        {/* Subscription success banner */}
        {subscribedBanner && (
          <div
            style={{
              background: "#f0fdf4",
              border: "1px solid #86efac",
              borderRadius: "10px",
              padding: "14px 18px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "20px" }}>🎉</span>
              <span style={{ fontSize: "14px", fontWeight: "600", color: "#15803d" }}>
                Subscription activated! Welcome aboard — your full access is now restored.
              </span>
            </div>
            <button
              onClick={() => setSubscribedBanner(false)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#6b7280",
                fontSize: "18px",
                lineHeight: 1,
                padding: "0 4px",
              }}
            >
              ×
            </button>
          </div>
        )}

        <div
          className="dashboard-card dashboard-hero-card"
          style={{
            marginBottom: "32px",
            padding: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 0, flex: "1 1 280px" }}>
              <h2
                className="dashboard-hero-title"
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "22px",
                  fontWeight: "700",
                }}
              >
                Business Overview
              </h2>
              <p className="dashboard-hero-subtitle" style={{ color: "#6b7280", fontSize: "14px", margin: 0 }}>
                Welcome to your business dashboard
              </p>
            </div>

            {(() => {
              const selBiz =
                businesses.find((b) => b._id === selectedBusinessId) ||
                businesses[0];
              const businessLocations = shops.filter(
                (s) => s.businessId === (selBiz?._id || selectedBusinessId),
              );
              const primaryLocationSlug = businessLocations[0]?.slug || null;
              const shareUrl =
                selBiz?.slug && primaryLocationSlug
                  ? `${window.location.origin}/business/${selBiz.slug}/${primaryLocationSlug}`
                  : selBiz?.slug
                    ? `${window.location.origin}/business/${selBiz.slug}`
                    : null;

              return (
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {/* Walk-in (Phase 1): R1/R2 cannot be the provider of record,
                      so `requireProvider` makes the professional a required choice.
                      `locations` is the caller's FULL location list on purpose - it
                      is the same list /dashboard/bookings passes, so an owner sees
                      the same options on both pages. `shopId` is only the default:
                      the business currently being viewed. R2 gets no picker. */}
                  <WalkInButton
                    requireProvider
                    shopId={businessLocations[0]?._id || null}
                    locations={shops}
                    role="R1"
                  />
                  {shareUrl ? (
                    <ShareProfileButton
                      url={shareUrl}
                      title={
                        selBiz?.name
                          ? `${selBiz.name} on Ogestyle`
                          : "My Business on Ogestyle"
                      }
                      label="Share Business Profile"
                    />
                  ) : null}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Go-Live Panel - Show when business exists */}
        {/* Note: GoLivePanel replaces OnboardingChecklist for businesses */}
        {!loading && selectedBusinessId && (
          <GoLivePanel
            type="business"
            id={selectedBusinessId}
            onGoLive={async () => {
              // Refresh data after go-live
              await fetchShops();
              await fetchOnboardingStatus();
              alert("🎉 Your business is now live! Trial period has started.");
            }}
          />
        )}

        {/* Trial Countdown - Show when business is live and has trial end date */}
        {!loading &&
          selectedBusinessId &&
          (() => {
            const selectedBusiness = businesses.find(
              (b) => b._id === selectedBusinessId,
            );
            // Only show trial countdown if subscription is NOT active
            return selectedBusiness?.trialEndDate &&
              selectedBusiness?.goLiveDate &&
              selectedBusiness?.subscriptionStatus !== "active" ? (
              <TrialCountdown
                trialEndDate={selectedBusiness.trialEndDate}
                userType="business"
                hasPaymentMethod={!!selectedBusiness.stripePaymentMethodId}
                onExpired={() => {
                  // Optionally trigger a refresh or redirect to subscription page
                }}
              />
            ) : null;
          })()}

        {/* Phase 3: Business Selector for multi-business owners */}
        {businesses.length > 0 && (
          <BusinessSelector
            businesses={businesses}
            selectedBusinessId={selectedBusinessId}
            onBusinessChange={setSelectedBusinessId}
          />
        )}

        {/* Professional Profile Banner - show only if owner has R1 but not R3 */}
        {userRoles.includes("R1") &&
          !userRoles.includes("R3") &&
          !userRoles.includes("R4") && (
            <ProfessionalProfileBanner
              onProfileEnabled={() => {
                // Reload page to refresh user roles after enabling
                window.location.reload();
              }}
            />
          )}

        {/* THE DAY, BEFORE THE COUNTERS (user, 2026-10-23: *"this will be above the four tiles total locations,
            active locations, pending approval and sms credits in R1"*). A shopkeeper opens this page to ask "what
            is happening today", which a count of locations cannot answer. R1 oversees every location, so it gets
            the picker — and with one location there is nothing to pick, so none is shown. */}
        <DayScheduleGrid
          locations={shops.map((location) => ({
            _id: location._id,
            name: location.name,
          }))}
          allowLocationPicker
          title="Today at a glance"
        />

        {/* Quick Stats */}
        <div
          className="dashboard-kpi-grid"
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "repeat(2, minmax(0, 1fr))"
              : "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "20px",
            marginBottom: "32px",
          }}
        >
          <div className="dashboard-card dashboard-kpi-card" style={{ padding: "24px" }}>
            <div
              className="dashboard-kpi-label"
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginBottom: "8px",
                textTransform: "uppercase",
                fontWeight: "600",
              }}
            >
              Total Locations
            </div>
            <div
              className="dashboard-kpi-value"
              style={{ fontSize: "32px", fontWeight: "700", color: "#111827" }}
            >
              {selectedBusinessId && businesses.length > 1
                ? shops.filter((s) => s.businessId === selectedBusinessId)
                    .length
                : shops.length}
            </div>
          </div>
          <div className="dashboard-card dashboard-kpi-card" style={{ padding: "24px" }}>
            <div
              className="dashboard-kpi-label"
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginBottom: "8px",
                textTransform: "uppercase",
                fontWeight: "600",
              }}
            >
              Active Locations
            </div>
            <div
              className="dashboard-kpi-value"
              style={{ fontSize: "32px", fontWeight: "700", color: "#059669" }}
            >
              {selectedBusinessId && businesses.length > 1
                ? shops.filter(
                    (s) => s.businessId === selectedBusinessId && s.approved,
                  ).length
                : shops.filter((s) => s.approved).length}
            </div>
          </div>
          <div className="dashboard-card dashboard-kpi-card" style={{ padding: "24px" }}>
            <div
              className="dashboard-kpi-label"
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginBottom: "8px",
                textTransform: "uppercase",
                fontWeight: "600",
              }}
            >
              Pending Approval
            </div>
            <div
              className="dashboard-kpi-value"
              style={{ fontSize: "32px", fontWeight: "700", color: "#f59e0b" }}
            >
              {shops.filter((s) => !s.approved).length}
            </div>
          </div>

          {/* SMS Credits Card */}
          <div
            className="dashboard-card dashboard-kpi-card"
            style={{
              padding: "24px",
              cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onClick={() => navigate("/dashboard/marketing/sms-credits")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div
              className="dashboard-kpi-label"
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginBottom: "8px",
                textTransform: "uppercase",
                fontWeight: "600",
              }}
            >
              SMS Credits
            </div>
            {loadingSmsCredits ? (
              <div style={{ fontSize: "16px", color: "#6b7280" }}>
                Loading...
              </div>
            ) : smsCredits ? (
              <div>
                <div
                  className="dashboard-kpi-value"
                  style={{
                    fontSize: "28px",
                    fontWeight: "700",
                    color: "#3b82f6",
                    marginBottom: "4px",
                  }}
                >
                  {smsCredits.purchasedCredits?.toLocaleString() || 0}
                </div>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>
                  Purchased Credits
                </div>
                {smsCredits.shops && smsCredits.shops.length > 0 && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#10b981",
                      marginTop: "8px",
                    }}
                  >
                    {smsCredits.shops
                      .reduce((sum, shop) => sum + (shop.freeCredits || 0), 0)
                      .toLocaleString()} {" "}
                    free credits across {smsCredits.shops.length} {" "}
                    {smsCredits.shops.length === 1 ? "shop" : "shops"}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: "16px", color: "#6b7280" }}>No data</div>
            )}
          </div>
        </div>

        {/* Locations Section */}
        <div
          style={{
            marginBottom: "24px",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            gap: "12px",
          }}
        >
          <h3
            className="dashboard-section-title"
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: "600",
              fontFamily: "Inter, sans-serif",
            }}
          >
            Your Locations
          </h3>
          <button
            className="btn-primary"
            style={{
              padding: "8px 16px", // Reduced from default
              fontSize: "13px", // Slightly smaller
              fontWeight: "600",
              fontFamily: "Inter, sans-serif",
              whiteSpace: "nowrap",
              width: "auto",
              maxWidth: "140px", // Prevent stretching
              alignSelf: isMobile ? "flex-start" : "auto",
            }}
            onClick={() => {
              setShowAddModal(true);
              setAddSuccess(false);
              setError("");
            }}
          >
            + Add Location
          </button>
        </div>

        {loading ? (
          <div
            className="dashboard-card"
            style={{ textAlign: "center", padding: "40px" }}
          >
            <p style={{ color: "#6b7280", fontSize: "14px" }}>Loading...</p>
          </div>
        ) : (
            selectedBusinessId && businesses.length > 1
              ? shops.filter((s) => s.businessId === selectedBusinessId)
                  .length === 0
              : shops.length === 0
          ) ? (
          <div
            className="dashboard-card"
            style={{ textAlign: "center", padding: "40px" }}
          >
            <p
              style={{
                color: "#6b7280",
                marginBottom: "16px",
                fontSize: "14px",
              }}
            >
              No locations found. Add your first location to get started!
            </p>
            <button
              className="btn-primary"
              onClick={() => {
                setShowAddModal(true);
                setAddSuccess(false);
                setError("");
              }}
            >
              + Add Your First Location
            </button>
          </div>
        ) : (
          <div
            className="dashboard-locations-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
              gap: "20px",
            }}
          >
            {/* Filter shops by selected business if multiple businesses exist */}
            {(selectedBusinessId && businesses.length > 1
              ? shops.filter((s) => s.businessId === selectedBusinessId)
              : shops
            ).map((shop) => {
              const proCount = proCounts[shop._id] || 0;
              return (
                <div
                  key={shop._id}
                  className="dashboard-card dashboard-location-card"
                  style={{ padding: "24px", position: "relative" }}
                >
                  {/* Big Pro Count */}
                  <div
                    style={{
                      position: "absolute",
                      top: 18,
                      right: 24,
                      background: "#f3f4f6",
                      borderRadius: "50%",
                      width: 54,
                      height: 54,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                      border: "2px solid #e5e7eb",
                      fontWeight: 700,
                      fontSize: 26,
                      color: "#3b82f6",
                      zIndex: 2,
                    }}
                    title="Number of professionals at this location"
                  >
                    {proCount}
                  </div>
                  <div style={{ marginBottom: "16px" }}>
                    <h3
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: "18px",
                        fontWeight: "600",
                      }}
                    >
                      {shop.name}
                    </h3>
                    <p
                      style={{
                        margin: "0",
                        color: "#6b7280",
                        fontSize: "14px",
                      }}
                    >
                      {Array.isArray(shop.type)
                        ? shop.type.map(formatBusinessType).join(", ")
                        : formatBusinessType(shop.type)}
                    </p>
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <p
                      style={{ fontSize: "14px", margin: 0, color: "#374151" }}
                    >
                      {capitalizeFirst(shop.street)}
                      <br />
                      {capitalizeFirst(shop.city)},{" "}
                      {capitalizeFirst(shop.state)} {shop.zip}
                    </p>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginBottom: "16px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        padding: "4px 12px",
                        borderRadius: "12px",
                        background: shop.approved ? "#ecfdf5" : "#fef3c7",
                        color: shop.approved ? "#059669" : "#92400e",
                        fontSize: "13px",
                        fontWeight: "500",
                      }}
                    >
                      {shop.approved ? "✓ Active" : "⏳ Pending"}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      paddingTop: "16px",
                      borderTop: "1px solid #e5e7eb",
                    }}
                  >
                    <button
                      onClick={() => navigate(`/dashboard/shop/${shop._id}`)}
                      className="btn-primary"
                      style={{ flex: 1, padding: "8px 16px", fontSize: "14px" }}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Location Modal - Using Locations page design */}
      {showAddModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
            overflow: "auto",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              boxShadow: "0 12px 42px rgba(0,0,0,0.18)",
              padding: isMobile ? "20px" : "32px 28px",
              maxWidth: 550,
              width: "100%",
              textAlign: "left",
              position: "relative",
              maxHeight: "90vh",
              overflow: "auto",
              fontFamily: "Inter, sans-serif",
              boxSizing: "border-box",
            }}
          >
            {!addSuccess ? (
              <>
                <h4
                  style={{
                    color: "#001d4a",
                    fontWeight: 700,
                    marginBottom: 18,
                    fontFamily: "Inter, sans-serif",
                    fontSize: "20px",
                  }}
                >
                  Add Location
                </h4>
                <form
                  onSubmit={handleAddShop}
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Business Type (select all that apply)
                    </label>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(200px, 1fr))",
                        gap: "12px",
                        padding: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        background: "#f9f9f9",
                      }}
                    >
                      {businessTypes.map((type) => (
                        <label
                          key={type.value}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            value={type.value}
                            checked={formData.type.includes(type.value)}
                            onChange={handleTypeChange}
                            style={{ marginRight: "8px" }}
                          />
                          {type.emoji} {type.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Location Mode
                    </label>
                    <select
                      name="locationMode"
                      value={formData.locationMode}
                      onChange={handleChange}
                      required
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        boxSizing: "border-box",
                      }}
                    >
                      <option value="hybrid">Hybrid</option>
                      <option value="commission_only">Commission Only</option>
                      <option value="booth_rental_only">Booth Rental Only</option>
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Street Address
                    </label>
                    <AddressAutocompleteInput
                      value={formData.street}
                      onChange={handleStreetChange}
                      onAddressSelect={handleAddressSelect}
                      placeholder="123 Main St"
                      required
                      inputStyle={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontWeight: "600",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        City
                      </label>
                      <input
                        name="city"
                        type="text"
                        placeholder="City"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontWeight: "600",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        State
                      </label>
                      <input
                        name="state"
                        type="text"
                        placeholder="IL"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontWeight: "600",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        ZIP
                      </label>
                      <input
                        name="zipCode"
                        type="text"
                        placeholder="60601"
                        value={formData.zipCode}
                        onChange={handleChange}
                        required
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontFamily: "Inter, sans-serif",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Phone Number
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Location Email
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="Optional (defaults to business email)"
                      value={formData.email}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        boxSizing: "border-box",
                      }}
                    />
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        margin: "4px 0 0 0",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Leave blank to use business email address
                    </p>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Location Timezone
                    </label>
                    <select
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleChange}
                      required
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontFamily: "Inter, sans-serif",
                        boxSizing: "border-box",
                        backgroundColor: "white",
                      }}
                    >
                      <option value="">Select your timezone...</option>
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        margin: "4px 0 0 0",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Choose the timezone for this location.
                    </p>
                  </div>
                  {error && (
                    <div
                      style={{
                        color: "#d32f2f",
                        marginTop: 8,
                        fontFamily: "Inter, sans-serif",
                        fontSize: "14px",
                      }}
                    >
                      {error}
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      marginTop: 18,
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      className="btn-secondary"
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      style={{
                        minWidth: 120,
                        fontFamily: "Inter, sans-serif",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-primary"
                      type="submit"
                      style={{
                        minWidth: 120,
                        fontFamily: "Inter, sans-serif",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Add Location
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h4
                  style={{
                    color: "#00796b",
                    fontWeight: 700,
                    marginBottom: 18,
                    fontFamily: "Inter, sans-serif",
                    fontSize: "20px",
                  }}
                >
                  Location Added Successfully!
                </h4>
                <p
                  style={{
                    fontSize: "16px",
                    marginBottom: 18,
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Would you like to add another location?
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    marginTop: 10,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setShowAddModal(false);
                      setAddSuccess(false);
                    }}
                    style={{
                      minWidth: 120,
                      fontFamily: "Inter, sans-serif",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    No, Close
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleAddAnother}
                    style={{
                      minWidth: 120,
                      fontFamily: "Inter, sans-serif",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Yes, Add Another
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Proration Confirmation Modal */}
      {showProrationModal && prorationData && newLocationData && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "500px",
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              fontFamily: "Inter, sans-serif",
            }}
          >
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>📍</div>
              <h3
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  margin: "0 0 12px",
                  color: "#111827",
                }}
              >
                Add {newLocationData.name} to Subscription?
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6b7280",
                  margin: "0 0 8px",
                }}
              >
                This location will be added to your subscription with prorated
                billing
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#059669",
                  background: "#d1fae5",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  margin: 0,
                }}
              >
                ✓ Location will remain private until payment is confirmed
              </p>
            </div>

            <div
              style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                padding: "20px",
                marginBottom: "24px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                }}
              >
                <span style={{ fontSize: "14px", color: "#6b7280" }}>
                  Monthly Price:
                </span>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#111827",
                  }}
                >
                  ${prorationData.monthlyPrice}/mo
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                }}
              >
                <span style={{ fontSize: "14px", color: "#6b7280" }}>
                  Billing Period:
                </span>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#111827",
                  }}
                >
                  {prorationData.daysRemaining}{" "}
                  {prorationData.daysRemaining === 1 ? "day" : "days"} remaining
                </span>
              </div>

              <div
                style={{
                  borderTop: "1px solid #e5e7eb",
                  paddingTop: "12px",
                  marginTop: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    ⚡ Immediate Charge:
                  </span>
                  <span
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#3b82f6",
                    }}
                  >
                    ${prorationData.immediateCharge.toFixed(2)}
                  </span>
                </div>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    margin: "0 0 12px",
                  }}
                >
                  Prorated for {prorationData.daysRemaining} days remaining in
                  current period
                </p>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ fontSize: "14px", color: "#6b7280" }}>
                    📅 New Monthly Total:
                  </span>
                  <span
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    ${prorationData.newMonthlyTotal}/mo
                  </span>
                </div>
              </div>
            </div>

            <p
              style={{
                fontSize: "13px",
                color: "#6b7280",
                textAlign: "center",
                margin: "0 0 24px",
                lineHeight: "1.6",
              }}
            >
              Starting {new Date(prorationData.nextBillingDate).toLocaleDateString()},
              you'll be charged the full monthly amount
            </p>

            {error && (
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  padding: "12px",
                  marginBottom: "16px",
                  color: "#dc2626",
                  fontSize: "14px",
                }}
              >
                {error}
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: "12px",
              }}
            >
              <button
                onClick={handleCancelProration}
                disabled={prorationLoading}
                style={{
                  flex: 1,
                  padding: "14px",
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: prorationLoading ? "not-allowed" : "pointer",
                  opacity: prorationLoading ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmProration}
                disabled={prorationLoading}
                style={{
                  flex: 1,
                  padding: "14px",
                  background: prorationLoading ? "#9ca3af" : "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: prorationLoading ? "not-allowed" : "pointer",
                }}
              >
                {prorationLoading
                  ? "Processing..."
                  : `Confirm & Pay $${prorationData.immediateCharge.toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </SidebarLayout>
  );
}

export default ShopDashboardPage;
